import type { SettingTab } from '@affine/core/modules/dialogs/constant';
import type { GlobalState } from '@affine/core/modules/storage/providers/global';
import { Service, useServiceOptional } from '@toeverything/infra';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

const LAST_PAGE = 'settings.lastPage';
const SUB_TABS = 'settings.subTabs';
const SCROLL = 'settings.scroll';

/** Scroll positions are written once scrolling rests this long. */
const SCROLL_REST_MS = 200;

/**
 * What the settings dialog keeps across openings and reloads: the page it
 * showed last, the sub-tab every page showed and every scroll position.
 */
export class SettingsMemory extends Service {
  constructor(private readonly state: GlobalState) {
    super();
  }

  get lastPage(): SettingTab | null {
    return this.state.get<SettingTab>(LAST_PAGE) ?? null;
  }

  rememberPage(tab: SettingTab) {
    this.state.set(LAST_PAGE, tab);
  }

  subTab(id: string): string | undefined {
    return this.state.get<Record<string, string>>(SUB_TABS)?.[id];
  }

  rememberSubTab(id: string, tab: string) {
    const tabs = this.state.get<Record<string, string>>(SUB_TABS);
    if (tabs?.[id] !== tab) this.state.set(SUB_TABS, { ...tabs, [id]: tab });
  }

  scroll(key: string): number {
    return this.state.get<Record<string, number>>(SCROLL)?.[key] ?? 0;
  }

  rememberScroll(key: string, top: number) {
    const positions = this.state.get<Record<string, number>>(SCROLL);
    if (positions?.[key] !== top)
      this.state.set(SCROLL, { ...positions, [key]: top });
  }
}

export interface ScrollMemory {
  /** The content of the current key is complete: show its position now. */
  settle: () => void;
  /** The caller placed the scroll position itself, e.g. at a requested section. */
  keep: () => void;
}

/**
 * Shows the remembered scroll position of `element` whenever `key` changes and
 * remembers every position the user scrolls to. While the content of a key is
 * still too short for its position, nothing is remembered until `settle`.
 * A `null` key neither shows nor remembers a position.
 */
export function useScrollMemory(
  element: HTMLElement | null,
  key: string | null
): ScrollMemory {
  const memory = useServiceOptional(SettingsMemory);
  const shown = useRef({ key, settled: false });
  const pending = useRef<{ key: string; top: number } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    const next = pending.current;
    pending.current = null;
    if (next) memory?.rememberScroll(next.key, next.top);
  }, [memory]);
  const show = useCallback(() => {
    const current = shown.current;
    if (!element || !memory || current.key === null) return;
    const top = memory.scroll(current.key);
    if (element.scrollTop !== top)
      element.scrollTo({ top, behavior: 'instant' });
    current.settled = element.scrollTop >= top - 1;
  }, [element, memory]);

  useLayoutEffect(() => {
    flush();
    shown.current = { key, settled: false };
    show();
  }, [key, flush, show]);

  useEffect(() => {
    if (!element) return;
    const onScroll = () => {
      const current = shown.current;
      if (!current.settled || current.key === null) return;
      // Read now: after the dialog closes the element no longer has a layout.
      pending.current = { key: current.key, top: element.scrollTop };
      clearTimeout(timer.current);
      timer.current = setTimeout(flush, SCROLL_REST_MS);
    };
    element.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      element.removeEventListener('scroll', onScroll);
      flush();
    };
  }, [element, flush]);

  return useMemo(
    () => ({
      settle: () => {
        if (shown.current.key !== key || shown.current.settled) return;
        show();
        shown.current.settled = true;
      },
      keep: () => {
        shown.current.settled = true;
      },
    }),
    [key, show]
  );
}
